import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FormDefinition, FormListResponse, FormUpdateRequest } from "@eduviaos/shared/types";
import { api } from "@/lib/api";

/**
 * Phase H1 FE — IA-side hooks for the tenant's `/forms` surface.
 *
 * Why this file exists
 * --------------------
 * Until H1 the IA only consumed forms transitively (the apply-portal
 * BE resolved them on the parent's behalf, and the per-enquiry
 * `/admissions/enquiries/{id}/active-form` returned a denormalised
 * `ActiveFormResponse`). Phase H1 adds two new IA call-sites that need
 * the underlying `FormDefinition` BEFORE an enquiry exists:
 *
 *   1. `EnquiryCreateSheet` — when the resolved initial stage has a
 *      linked form, the IA must render that form (via `<FormRenderer>`)
 *      so the H1 BE-2 contract — `stage_payloads[initial_stage]` is
 *      mandatory — is satisfied at create-time.
 *   2. `EnquiryDetailPanel` — the "Allow next stage" CTA needs to know
 *      whether the *next* stage even has a linked form (otherwise the
 *      transition would unlock a no-op, which is just confusing).
 *
 * Both call-sites need a `purpose` + `purpose_ref_id` lookup against
 * the active version, which `GET /forms?purpose=...&purpose_ref_id=...`
 * already supports. We expose it as `useFormForStage(stageKey)` so the
 * call-site stays declarative.
 */

export const formsKeys = {
  all: ["forms"] as const,
  list: () => [...formsKeys.all, "list"] as const,
  detail: (slug: string) => [...formsKeys.all, "detail", slug] as const,
  byPurpose: (purpose: string, refId: string | null | undefined) =>
    [...formsKeys.all, "by-purpose", purpose, refId ?? "_none"] as const,
};

interface UseFormForStageOptions {
  /** When false, the query is parked. Use this for "render the form
   * only when the user actually opens the sheet" so we don't burn a
   * GET on every list-page mount. */
  enabled?: boolean;
}

/**
 * Resolve the active `FormDefinition` linked to one admission stage.
 *
 * Returns:
 *   - `data: FormDefinition | null` — the active version's
 *     `FormDefinition` if a form is linked, else `null` (NOT undefined,
 *     so callers can branch with `data === null` to render the
 *     no-form-needed path).
 *   - `isLoading` — true while the request is in flight.
 *
 * The BE already filters to `is_active_version=True` server-side, so
 * we don't need to dedupe versions client-side. The response is sorted
 * by `updated_at desc` — we take `items[0]` (and warn if there's more
 * than one, which would indicate a BE bug because purpose+purpose_ref
 * is a unique active-tuple).
 */
export function useFormForStage(stageKey: string | null, opts: UseFormForStageOptions = {}) {
  return useQuery<FormDefinition | null>({
    queryKey: formsKeys.byPurpose("admission_stage", stageKey),
    queryFn: async () => {
      const search = new URLSearchParams({
        purpose: "admission_stage",
        purpose_ref_id: stageKey as string,
        limit: "1",
      });
      const resp = await api.get<FormListResponse>(`/forms?${search.toString()}`);
      return resp.items[0] ?? null;
    },
    enabled: Boolean(stageKey) && (opts.enabled ?? true),
    // Forms are tenant-config — they change infrequently within a
    // session. 60s lines up with the funnel hook's stale window so a
    // single SA edit propagates to both lookups together.
    staleTime: 60 * 1000,
  });
}

/**
 * Phase F-finale-5 Slice 5a — tenant-self forms-management hooks.
 *
 * Power the IA `/forms` list + `/forms/{slug}/edit` editor. Tenant is
 * resolved BE-side from the `X-Tenant-Slug` header + JWT, so these hit
 * the bare `/forms` surface (NOT the SA `/super-admin/tenants/{slug}/...`
 * routes). Reads need `VIEW_FORMS`; the PATCH needs `MANAGE_FORMS` +
 * `forms_editable_by_tenant` (enforced server-side; the FE additionally
 * gates the UI via `useCanEditForms`).
 */

/** List every form owned by the current tenant (all active versions). */
export function useFormsList() {
  return useQuery<FormListResponse>({
    queryKey: formsKeys.list(),
    queryFn: () => api.get<FormListResponse>("/forms"),
    staleTime: 15_000,
  });
}

/** Resolve the active `FormDefinition` for one of the tenant's forms by slug. */
export function useForm(formSlug: string) {
  return useQuery<FormDefinition>({
    queryKey: formsKeys.detail(formSlug),
    queryFn: () => api.get<FormDefinition>(`/forms/${formSlug}`),
    enabled: Boolean(formSlug),
    staleTime: 15_000,
  });
}

/**
 * PATCH a form — every edit mints a new active version on the BE. On
 * success we seed the detail cache with the fresh version (which the
 * editor's `syncedVersionRef` effect re-hydrates from) and bust the list
 * so field counts / `updated_at` refresh.
 */
export function useUpdateForm(formSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: FormUpdateRequest) => api.patch<FormDefinition>(`/forms/${formSlug}`, req),
    onSuccess: (form) => {
      qc.setQueryData(formsKeys.detail(formSlug), form);
      void qc.invalidateQueries({ queryKey: formsKeys.list() });
    },
  });
}
